import { isFresh, getAwsS3SignedUrl } from './api';
import { MediaType } from './index';

// load as a result of a dashboard or card query
export const ADD_ALL_MEDIA = 'ADD_ALL_MEDIA';
export const UPDATE_MEDIA_EVENT_IDS = 'UPDATE_MEDIA_EVENT_IDS';
// update a media definition (when you get an update over mqtt)
export const UPDATE_MEDIA = 'UPDATE_MEDIA';
export const UPDATE_MEDIA_STATUS = 'UPDATE_MEDIA_STATUS';
export const CLEAR_REQUEST_IN_PROGRESS = 'CLEAR_REQUEST_IN_PROGRESS';
export const REQUEST_SIGNED_URL = 'REQUEST_SIGNED_URL';
export const REQUEST_SIGNED_URL_ERROR = 'REQUEST_SIGNED_URL_ERROR';
export const UPDATE_SIGNED_URL = 'UPDATE_SIGNED_URL';
export const VIDEO_PLAYBACK_ERROR = 'VIDEO_PLAYBACK_ERROR';
export const CLEAR_PLAYBACK_ERROR = 'CLEAR_PLAYBACK_ERROR';

export const REQUEST_MEDIA_FROM_RAVEN = 'REQUEST_MEDIA_FROM_RAVEN';
export const REQUEST_MEDIA_FROM_RAVEN_ERROR = 'REQUEST_MEDIA_FROM_RAVEN_ERROR';
export const REQUEST_MEDIA_FROM_RAVEN_SUCCESS = 'REQUEST_MEDIA_FROM_RAVEN_SUCCESS';

export const addAllMedia = (ravenUuid, mediaList) => ({
    type: ADD_ALL_MEDIA,
    payload: {
        ravenUuid, mediaList: mediaList.map(m => ({
            ...m,
            _publicUrl: (m.key && m.key.startsWith('raven-apps/')) && `https://raven-apps.s3.amazonaws.com/${m.key.substring('raven-apps/'.length)}`
        }))
    }
});

export const updateMedia = (ravenUuid, media) => ({
    type: UPDATE_MEDIA,
    payload: {
        ravenUuid,
        media: {
            ...media,
            _publicUrl: (media.key && media.key.startsWith('raven-apps/')) && `https://raven-apps.s3.amazonaws.com/${media.key.substring('raven-apps/'.length)}`
        }
    }
});

// clearRequestInProgress
const clearRequestInProgress = (ravenUuid, sourceMediaId, productMediaId, cameraType, mediaType) => ({
    type: CLEAR_REQUEST_IN_PROGRESS,
    payload: { ravenUuid, sourceMediaId, productMediaId, cameraType, mediaType }
});

export const requestSignedUrl = (ravenUuid, mediaId) => ({
    type: REQUEST_SIGNED_URL,
    payload: { ravenUuid, mediaId }
});

export const requestSignedUrlError = (ravenUuid, mediaId, err) => ({
    type: REQUEST_SIGNED_URL_ERROR,
    payload: { ravenUuid, mediaId, err }
});

export const updateSignedUrl = (ravenUuid, mediaId, signedUrl) => ({
    type: UPDATE_SIGNED_URL,
    payload: { ravenUuid, mediaId, signedUrl }
});

export const requestMediaFromRaven = (ravenUuid, request, timeoutId) => ({
    type: REQUEST_MEDIA_FROM_RAVEN,
    payload: {
        ravenUuid,
        mediaId: request.sourceMediaId,
        request,
        timeoutId
    }
});

const requestMediaFromRavenSuccess = (ravenUuid, sourceMediaId) => ({
    type: REQUEST_MEDIA_FROM_RAVEN_SUCCESS,
    payload: { ravenUuid, mediaId: sourceMediaId }
});

export const requestMediaFromRavenError = ({ ravenUuid, mediaId, err }) => ({
    type: REQUEST_MEDIA_FROM_RAVEN_ERROR,
    payload: { ravenUuid, mediaId, err }
});

const updateMediaStatus = ({ ravenUuid, mediaId, status, statusTime }) => ({
    type: UPDATE_MEDIA_STATUS,
    payload: { ravenUuid, mediaId, status, statusTime }
});

export const updateMediaEventIds = (ravenUuid, eventList) => ({
    type: UPDATE_MEDIA_EVENT_IDS,
    payload: { ravenUuid, eventList }
});

export const videoPlaybackError = (ravenUuid, mediaId, error) => ({
    type: VIDEO_PLAYBACK_ERROR,
    payload: { ravenUuid, mediaId, error }
});

export const clearPlaybackError = (ravenUuid, mediaId) => ({
    type: CLEAR_PLAYBACK_ERROR,
    payload: { ravenUuid, mediaId }
});

const getMediaFromState = (ravenUuid, mediaId, getState) =>
    getState().data.media.mediaMap.getIn([ravenUuid, mediaId]);

export const refreshSignedUrlIfNeeded = (ravenUuid, mediaId) => (dispatch, getState) => {
    const media = getMediaFromState(ravenUuid, mediaId, getState);
    if(!media) {
        return Promise.reject(new Error(`media ${ravenUuid}.${mediaId} not found in store`));
        // console.warn(`media ${ravenUuid}.${mediaId} not found in store`);
        // return Promise.resolve();
    }
    if(!media.get('key')) {
        // no key => no file => no work to do
        return Promise.resolve();
    }
    // do not proceed if we have a fresh url (at least 10 minutes until expiry)
    if (isFresh(media.get('signedUrl'))) {
        console.log(`media ${ravenUuid}.${mediaId} signedUrl is fresh`);
        return Promise.resolve();
    }
    // do not proceed if a request is in progress
    if (media.get('signedUrlRequestInProgress')) {
        console.log(`media ${ravenUuid}.${mediaId} signedUrlRequestInProgress`);
        return Promise.resolve();
    }
    dispatch(requestSignedUrl(ravenUuid, mediaId));
    return getAwsS3SignedUrl({ key: media.get('key') }).then(
        result => dispatch(updateSignedUrl(ravenUuid, mediaId, result)),
        err => dispatch(requestSignedUrlError(ravenUuid, mediaId, err))
    );
};

const MAX_TIME_UNTIL_MEDIA_INITIATED_MS = 15000;

export const createPreviewVideo = (mqttService, ravenUuid, sourceMediaId) => (dispatch, getState) => {
    if(!mqttService) return Promise.reject(new Error('mqttService is required by createPreviewVideo'));
    const media = getMediaFromState(ravenUuid, sourceMediaId, getState);
    if(!media) {
        return Promise.reject(new Error(`media ${ravenUuid}.${sourceMediaId} not found in store`));
    }
    // the mediaRequest sent to Raven
    const request = {
        sourceMediaId,
        mediaType: MediaType.MEDIA_PREVIEW_VIDEO,
    };
    // do not proceed if a request is in progress
    if (media.get('requestInProgress')) {
        console.log(`media ${ravenUuid}.${sourceMediaId} requestInProgress`);
        return Promise.resolve();
    }
    dispatch(requestMediaFromRaven(
        ravenUuid,
        request,
        setTimeout(() => {
            dispatch(requestMediaFromRavenError({
                ravenUuid,
                mediaId: sourceMediaId,
                err: new Error('timeout waiting for EVENT_MEDIA_INITIATED')
            }));
        }, MAX_TIME_UNTIL_MEDIA_INITIATED_MS)
    ));
    return mqttService.sendMediaRequest({ ravenUuid, request }).then(() => {
        dispatch(requestMediaFromRavenSuccess(
            ravenUuid,
            sourceMediaId,
        ));
    }).catch(err => {
        console.error(err);
        const sourceMedia = getMediaFromState(ravenUuid, sourceMediaId, getState);
        if(sourceMedia && sourceMedia.get('timeoutId')) {
            // prevent the timeout from clobbering an mqtt error
            clearTimeout(sourceMedia.get('timeoutId'));
        }
        dispatch(requestMediaFromRavenError({
            ravenUuid,
            mediaId: sourceMediaId,
            timeoutId: null,
            err
        }));
    });
};

export const linkMediaToSourceMediaEvent = (ravenUuid, mediaId, sourceMediaId, addMediaIdToEvent) => (dispatch, getState) => {
    dispatch(updateMedia(ravenUuid, { mediaId, sourceMediaId }));
    const sourceMedia = getMediaFromState(ravenUuid, sourceMediaId, getState);
    if(!sourceMedia) {
        return Promise.reject(new Error(`source media ${ravenUuid}.${sourceMediaId} not found in store`));
    }
    if(sourceMedia.get('timeoutId')) {
        clearTimeout(sourceMedia.get('timeoutId'));
        dispatch(updateMedia(ravenUuid, { mediaId: sourceMediaId, timeoutId: null}));
    }
    if(!sourceMedia.get('eventIds')) {
        return Promise.reject(new Error(`source media ${ravenUuid}.${sourceMediaId} has no linked events`));
    }
    dispatch(updateMedia(ravenUuid, { mediaId, eventIds: sourceMedia.get('eventIds') }));
    sourceMedia.get('eventIds').forEach(eventId => dispatch(addMediaIdToEvent(ravenUuid, eventId, mediaId)));
    return Promise.resolve();
};

export const updateMediaStatusWithRequestInProgressCheck = (ravenUuid, mediaId, status, statusTime) => (dispatch, getState) => {
    dispatch(updateMediaStatus({ ravenUuid, mediaId, status, statusTime }));
    const media = getMediaFromState(ravenUuid, mediaId, getState);
    if(!media) {
        // this happens the first time the media is added. We can be sure there is no 'type'
        return Promise.resolve();
    }
    const sourceMediaId = media.get('sourceMediaId');
    if (sourceMediaId) {
        const sourceMedia = getMediaFromState(ravenUuid, sourceMediaId, getState);
        if(!sourceMedia) {
            return Promise.reject(new Error(`source media ${ravenUuid}.${sourceMediaId} not found in store`));
        }
        const requestInProgress = sourceMedia.get('requestInProgress');
        if(requestInProgress) {
            dispatch(clearRequestInProgress(
                ravenUuid,
                sourceMediaId,
                mediaId,
                sourceMedia.get('cameraType'),
                requestInProgress.get('mediaType')
            ));
        }
    }
    return Promise.resolve();
};
